import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { NotebookPen, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { API } from "@/lib/api";

export default function NotesGallery({ slug, count }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  if (!count) return null;
  const src = (i) => `${API}/courses/${slug}/sample-notes/${i}`;
  const openAt = (i) => { setIdx(i); setOpen(true); };
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + count) % count); };
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % count); };

  return (
    <div className="wobble-group mt-6 rounded-md border-2 border-dashed border-[#D97706] bg-gradient-to-r from-[#FEF3C7] to-white p-4 md:p-5" data-testid="notes-gallery">
      <div className="mb-3 flex items-start gap-3">
        <div className="wobble flex h-12 w-12 shrink-0 -rotate-3 items-center justify-center rounded-sm border border-[#FDE68A] bg-white shadow-md">
          <NotebookPen size={24} className="text-[#D97706]" />
        </div>
        <div>
          <span className="inline-flex items-center gap-1 rounded-sm bg-[#16A34A] px-2 py-0.5 text-[11px] font-bold uppercase text-white">Free Sample · Click to zoom</span>
          <h3 className="mt-1 font-serif text-lg font-bold text-[#0B2545] md:text-xl">Peek inside: Sample Handwritten Notes</h3>
          <p className="text-sm text-gray-600">Tap any page to zoom in — see how clear, visual and exam-ready these notes are!</p>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            onClick={() => openAt(i)}
            className={`group relative shrink-0 overflow-hidden rounded-sm border border-[#D1D5DB] bg-white shadow-sm transition-transform hover:-translate-y-1 hover:shadow-lg ${i % 2 ? "rotate-1" : "-rotate-1"}`}
            data-testid={`notes-thumb-${i}`}
          >
            <img src={src(i)} alt={`Sample note page ${i + 1}`} loading="lazy" className="h-40 w-56 bg-white object-cover object-top" />
            <span className="absolute inset-0 flex items-center justify-center transition-colors group-hover:bg-[#0B2545]/25">
              <Maximize2 size={22} className="text-white opacity-0 transition-opacity group-hover:opacity-100" />
            </span>
            <span className="absolute bottom-1 right-1 rounded-sm bg-[#0B2545]/80 px-1.5 text-[10px] font-semibold text-white">{i + 1}/{count}</span>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl border-0 bg-transparent p-0 shadow-none" data-testid="notes-lightbox">
          <div className="relative">
            <img src={src(idx)} alt={`Sample note page ${idx + 1}`} className="max-h-[82vh] w-full rounded-md bg-white object-contain" />
            {count > 1 && (
              <>
                <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-[#0B2545]/80 p-2 text-white hover:bg-[#0B2545]" data-testid="notes-prev"><ChevronLeft size={22} /></button>
                <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[#0B2545]/80 p-2 text-white hover:bg-[#0B2545]" data-testid="notes-next"><ChevronRight size={22} /></button>
              </>
            )}
            <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-sm bg-[#0B2545]/80 px-3 py-1 text-xs font-semibold text-white">Page {idx + 1} of {count}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
